import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import OTPInput from '../components/common/OTPInput';
import Button from '../components/common/Button';
import { useAuth } from '../context/AuthContext';

const Signup = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        phoneNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: '',
        role: 'ADMIN', // Default role
    });
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [resendDisabled, setResendDisabled] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    useEffect(() => {
        // Get phone number from location state if available
        if (location.state?.phoneNumber) {
            setFormData(prev => ({ ...prev, phoneNumber: location.state.phoneNumber }));
            // Don't automatically send OTP - let user fill the form first
        }
    }, [location]);

    const startResendTimer = () => {
        setResendDisabled(true);
        setResendTimer(30);
        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setResendDisabled(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateAge = (dateOfBirth) => {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age >= 18;
    };

    // Phone number validation
    const validatePhoneNumber = (phone) => {
        return /^\d{10}$/.test(phone);
    };

    const validateForm = () => {
        if (!validatePhoneNumber(formData.phoneNumber)) {
            setError('Phone number must be exactly 10 digits');
            return false;
        }
        if (!formData.firstName.trim()) {
            setError('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            setError('Last name is required');
            return false;
        }
        if (!formData.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!formData.dateOfBirth) {
            setError('Date of birth is required');
            return false;
        }
        if (!validateAge(formData.dateOfBirth)) {
            setError('You must be at least 18 years old');
            return false;
        }
        if (!otp || otp.length !== 6) {
            setOtpError('Please enter a valid 6-digit OTP');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError('');
        setOtpError('');
        try {
            const signupData = {
                ...formData,
                otp
            };
            const response = await authService.signup(signupData);
            if (response.success) {
                // Store the token and update auth state
                login(response.data.token);
                navigate('/home');
            } else {
                setError(response.message || 'Failed to create account');
            }
        } catch (err) {
            setError(err.message || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendDisabled) return;

        setLoading(true);
        setError('');
        try {
            const response = await authService.resendOtp(formData.phoneNumber);
            if (response.success) {
                startResendTimer();
            } else {
                setError(response.message || 'Failed to resend OTP');
            }
        } catch (err) {
            setError(err.message || 'Failed to resend OTP');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-800">Welcome to HairQure Admin</h1>
                </div>
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Create your account
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Please fill in your details
                    </p>
                </div>

                <div className="mt-8 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number
                            </label>
                            <input
                                id="phoneNumber"
                                name="phoneNumber"
                                type="tel"
                                required
                                className="input"
                                placeholder="Phone Number"
                                value={formData.phoneNumber}
                                onChange={(e) => {
                                    // Only allow digits
                                    const value = e.target.value.replace(/\D/g, '');
                                    setFormData(prev => ({ ...prev, phoneNumber: value }));
                                    if (value.length > 10) setError('Phone number cannot exceed 10 digits');
                                    else setError('');
                                }}
                                maxLength={10}
                                disabled={!!location.state?.phoneNumber}
                            />
                        </div>
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                                First Name
                            </label>
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                required
                                className="input"
                                placeholder="First Name"
                                value={formData.firstName}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                                Last Name
                            </label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                required
                                className="input"
                                placeholder="Last Name"
                                value={formData.lastName}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                className="input"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
                                Date of Birth
                            </label>
                            <input
                                id="dateOfBirth"
                                name="dateOfBirth"
                                type="date"
                                required
                                className="input"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                                User Role
                            </label>
                            <select
                                id="role"
                                name="role"
                                required
                                className="input"
                                value={formData.role}
                                onChange={handleInputChange}
                            >
                                <option value="ADMIN">Admin</option>
                                <option value="DOCTOR">Doctor</option>
                                <option value="STAFF">Staff</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                OTP Verification
                            </label>
                            <OTPInput
                                length={6}
                                value={otp}
                                onChange={setOtp}
                                error={otpError}
                            />
                            <div className="flex items-center justify-between mt-2">
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendDisabled}
                                    className={`text-sm font-medium ${
                                        resendDisabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-indigo-600 hover:text-indigo-500'
                                    }`}
                                >
                                    {resendDisabled
                                        ? `Resend OTP in ${resendTimer}s`
                                        : 'Resend OTP'}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div className="rounded-md bg-red-50 p-4">
                                <p className="text-sm text-red-600 text-center">{error}</p>
                            </div>
                        )}
                        {/* Navigation to Login */}
                        <div className="text-center">
                            <p className="text-sm text-gray-600">
                                Already have an account?{' '}
                                <button
                                    type="button"
                                    onClick={() => navigate('/login')}
                                    className="font-medium text-indigo-600 hover:text-indigo-500"
                                >
                                    Login here
                                </button>
                            </p>
                        </div>
                    </div>

                    <div>
                        <Button
                            onClick={handleSubmit}
                            loading={loading}
                            variant="primary"
                            className="w-full"
                        >
                            Create Account
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup; 